import * as React from 'react'
import { useCallback, useState, useMemo } from 'react'

import { OpportunityProgressWidgetProps } from './typings'
import { colors } from './styling'
import { useUpdateOppStatus } from '../../hooks'

import { ClosedWonLostMenu } from './ClosedWonLostMenu/ClosedWonLostMenu'

import { Chevron } from '@sales-center/components'
import { ProgressWidget, ProgressItem as IProgressItem } from './ProgressWidget'

import { useSelectOppStages } from '@user/store/salesFunnel'
import { useTheme } from '@mui/material/styles'

export function OpportunityProgressWidget(
  props: OpportunityProgressWidgetProps,
) {
  const { funnelStageId, opportunityId, salesFunnelId } = props

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const wonLostMenuOpen = Boolean(anchorEl)

  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(
    null,
  )

  const [salesFunnelStages, closedWonStageId, closedLostStageId] =
    useSelectOppStages(salesFunnelId)

  const { loading, onChangeStatus } = useUpdateOppStatus(
    opportunityId,
    closedWonStageId,
    closedLostStageId,
  )

  const handleStatusClick = useCallback(
    (value: number | string | null) =>
      void (typeof value === 'number' && onChangeStatus(value)),
    [onChangeStatus],
  )

  const handleClosedWonMenuItemClick = useCallback(() => {
    setActiveSegmentIndex(null)
    onChangeStatus(closedWonStageId)
    setAnchorEl(null)
  }, [closedWonStageId, onChangeStatus])

  const handleClosedLostMenuItemClick = useCallback(() => {
    setActiveSegmentIndex(null)
    onChangeStatus(closedLostStageId)
    setAnchorEl(null)
  }, [closedLostStageId, onChangeStatus])

  const opacity = loading ? 0.5 : 1

  const items = useMemo(() => {
    let stageComplete: boolean = funnelStageId !== undefined
    const tempItems: IProgressItem[] = []

    // non-closed
    salesFunnelStages
      .filter((s) => s.name !== 'Closed/Won' && s.name !== 'Closed/Lost')
      .map((stage) => {
        tempItems.push({
          isComplete: stageComplete,
          text: stage.name,
          value: stage.id,
          dataTestId: `opportunityCardStage-${opportunityId}-${stage.id}`,
        })
        if (stage.id === funnelStageId) {
          stageComplete = false
        }
      })

    const isClosed: boolean =
      funnelStageId === closedWonStageId || funnelStageId === closedLostStageId

    tempItems.push({
      isComplete: isClosed,
      text: 'Closed Won / Closed Lost',
      value: `${closedWonStageId}|${closedLostStageId}`,
      endAdornment: (open) => (
        <Chevron
          open={open}
          color="#a9aaab"
          dataTestId={`opportunityCardClosedLostWonChevron-${opportunityId}`}
        />
      ),
      onClick: (e) => setAnchorEl(e.currentTarget),
      dataTestId: 'closedLostWonTempBtn',
    })

    return tempItems
  }, [
    closedLostStageId,
    closedWonStageId,
    funnelStageId,
    opportunityId,
    salesFunnelStages,
  ])

  const lastCompleteIndex = useMemo(
    () =>
      Math.max(
        0,
        items.findIndex((i) => i.value === funnelStageId),
      ),
    [funnelStageId, items],
  )

  const disabled =
    props.disabled ||
    funnelStageId === closedLostStageId ||
    funnelStageId === closedWonStageId
  const actualColors = colors(useTheme())
  return (
    <>
      <ProgressWidget
        colors={actualColors}
        disabled={disabled}
        height={33}
        activeSegmentIndex={activeSegmentIndex}
        onActiveIndexChange={setActiveSegmentIndex}
        opacity={opacity}
        segmentMinWidth={250}
        segmentSpacing={6}
        transitionTime={0.5}
        items={items}
        onChange={handleStatusClick}
        scrollTo={lastCompleteIndex}
      />

      {/* ========== C L O S E D   W O N / L O S T   M E N U ========== */}
      <ClosedWonLostMenu
        open={wonLostMenuOpen}
        onClose={() => setAnchorEl(null)}
        onClosedLostClick={handleClosedLostMenuItemClick}
        onClosedWonClick={handleClosedWonMenuItemClick}
        opportunityId={opportunityId}
        anchorEl={anchorEl}
      />
    </>
  )
}
